import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { resetPasswordSchema, type ResetPasswordInput } from '@orion/contracts';
import { Button, Input } from '@orion/ui';
import { useAuth } from '../../lib/auth.js';
import { AuthShell, AuthStepCard } from './AuthShell.js';

/** docs/architecture/05 § invites — landing page for the link emailed by
 * UsersAdminService#invite; sets the invited user's password and, on
 * success, logs them straight into the workspace. */
export function AcceptInvitePage() {
  const { token } = useSearch({ from: '/accept-invite' });
  const { acceptInvitation, acceptInvitationError } = useAuth();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { token, newPassword: '' },
  });

  const onSubmit = async (input: ResetPasswordInput) => {
    await acceptInvitation(input);
    await navigate({ to: '/' });
  };

  if (!token) {
    return (
      <AuthShell>
        <AuthStepCard>
          <h1 className="text-[17px] leading-6 font-[650] tracking-[-0.01em] text-ink">Invalid invite link</h1>
          <p className="text-[13px] leading-4.5 text-text-secondary">This link is missing its invite token. Ask your admin to resend the invitation.</p>
        </AuthStepCard>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthStepCard>
        <h1 className="text-[17px] leading-6 font-[650] tracking-[-0.01em] text-ink">Set your password</h1>
        <p className="text-[13px] leading-4.5 text-text-secondary">Choose a password to activate your account and sign in.</p>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
          <input type="hidden" {...register('token')} />
          <Input
            label="New password"
            type="password"
            uiSize="lg"
            autoComplete="new-password"
            error={errors.newPassword?.message}
            {...register('newPassword')}
          />

          {acceptInvitationError && (
            <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
              {acceptInvitationError}
            </div>
          )}

          <Button type="submit" size="lg" className="w-full" loading={isSubmitting}>
            Set password &amp; sign in
          </Button>
        </form>
      </AuthStepCard>
    </AuthShell>
  );
}
